// =====================================================
// Lizzi — capa de acceso a datos reales (Supabase)
//
// Traduce filas de la BD (snake_case) al shape que las pantallas
// esperan (camelCase, compatible con el antiguo MockData).
// Las partes que son CONFIGURACIÓN de producto y no datos de la
// organización (asistentes, etiquetas de estado, índice de memoria,
// ficha de análisis SANDETEL) se mantienen como contenido estático.
// =====================================================
import { supabase } from '../integrations/supabase/client';

import { MOCK_ASSISTANTS } from '../mock/assistants.js';
import { STATUS_LABELS, STATUS_FLOW } from '../mock/status.js';


// ---------- helpers ----------
function initialsFrom(name) {
  if (!name) return '·';
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || '·';
}

function daysUntil(dateStr) {
  if (!dateStr) return 0;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.ceil((d.getTime() - now.getTime()) / 86_400_000);
}

function relativeTime(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return 'Justo ahora';
  if (mins < 60) return `Hace ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `Hace ${hours} h`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'Ayer';
  return `Hace ${days} días`;
}

// ---------- mappers ----------
export function mapBid(r) {
  return {
    id: r.id,
    code: r.code,
    lot: r.lot,
    lotNumber: r.lot_number ?? null,
    parentBidId: r.parent_bid_id || null,
    title: r.title,
    shortTitle: r.short_title || null,
    entity: r.entity,
    entityShort: r.entity_short,
    region: r.region,
    company: r.company || null,
    location: r.location || null,
    cpv: r.cpv,
    status: r.status,
    analysisStatus: r.analysis_status || 'idle',
    analysisPhase: r.analysis_phase || null,
    analysisPct: typeof r.analysis_pct === 'number' ? r.analysis_pct : 0,
    analysisError: r.analysis_error || null,
    analysisGeneratedAt: r.analysis_generated_at || null,
    analysisModel: r.analysis_model || null,
    analysisSourceUrl: r.analysis_source_url || null,
    budgetBase: Number(r.budget_base) || 0,
    budgetVAT: Number(r.budget_vat) || 0,
    estimatedValue: Number(r.estimated_value) || 0,
    durationMonths: r.duration_months,
    extensions: r.extensions,
    deadline: r.deadline,
    deadlineDays: daysUntil(r.deadline),
    submittedAt: r.submitted_at,
    score: r.score,
    minScoreTechnical: r.min_score_technical,
    maxScoreTechnical: r.max_score_technical,
    techSubjective: r.tech_subjective,
    techAutomatic: r.tech_automatic,
    riskLevel: r.risk_level,
    estimatedCost: Number(r.estimated_cost) || 0,
    margin: Number(r.margin) || 0,
    teamLead: r.team_lead,
    team: r.team || [],
    progress: r.progress || 0,
    tags: r.tags || [],
    documents: r.documents || 0,
    flagship: !!r.flagship,
    goData: r.go_data || {},
    lastActivity: relativeTime(r.updated_at),
  };
}

export function mapAgent(r) {
  return {
    id: r.id,
    slug: r.slug,
    name: r.name,
    area: r.area || '',
    phase: r.phase ?? null,
    tagline: r.tagline || '',
    summary: r.summary || '',
    tone: r.tone || '',
    icon: r.icon || 'Sparkles',
    color: r.color || 'sem-info',
    systemPrompt: r.system_prompt || '',
    outputTemplate: Array.isArray(r.output_template) ? r.output_template : [],
    defaultModel: r.default_model || 'auto',
    enabled: r.enabled !== false,
    sortOrder: r.sort_order ?? 0,
    updatedAt: r.updated_at || null,
    createdAt: r.created_at || null,
  };
}

// Carga los agentes de forma resiliente: si la tabla `agents` aún no existe
// (migración no aplicada todavía), cae al catálogo por defecto sin tumbar el
// resto del dataset. Nunca lanza.
async function loadAgentsSafe() {
  let agents;
  try {
    const { data, error } = await supabase.from('agents').select('*').order('sort_order', { ascending: true });
    if (error) throw error;
    agents = (data || []).map(mapAgent);
  } catch (e) {
    console.warn('[lizzi] tabla agents no disponible aún, usando catálogo por defecto:', e?.message || e);
    return MOCK_ASSISTANTS;
  }
  // Adjunta el conteo de conocimiento por asistente (resiliente: si la tabla
  // aún no existe —F2 sin desplegar— deja los contadores a 0).
  try {
    const { data: srcs, error } = await supabase.from('knowledge_sources').select('agent_id, status');
    if (error) throw error;
    const total = {}; const ready = {};
    (srcs || []).forEach((s) => {
      total[s.agent_id] = (total[s.agent_id] || 0) + 1;
      if (s.status === 'ready') ready[s.agent_id] = (ready[s.agent_id] || 0) + 1;
    });
    agents.forEach((a) => { a.knowledgeCount = total[a.id] || 0; a.knowledgeReady = ready[a.id] || 0; });
  } catch (_) {
    agents.forEach((a) => { a.knowledgeCount = 0; a.knowledgeReady = 0; });
  }
  return agents;
}

function mapUser(p, roleMap) {
  return {
    id: p.user_id,
    name: p.display_name || 'Usuario',
    initials: initialsFrom(p.display_name),
    role: roleMap[p.user_id] || 'viewer',
    title: p.title || '',
    hue: p.avatar_hue ?? 200,
    email: undefined,
  };
}

// Prioridad de rol para mostrar el "rol principal"
const ROLE_RANK = { admin: 5, firmante: 4, revisor: 3, redactor: 2, viewer: 1 };
function primaryRole(roles) {
  if (!roles || roles.length === 0) return 'viewer';
  return [...roles].sort((a, b) => (ROLE_RANK[b] || 0) - (ROLE_RANK[a] || 0))[0];
}

// =====================================================
// Carga completa del dataset real para el usuario actual
// =====================================================
export async function loadRealData({ user, profile, roles }) {
  const [
    profilesRes,
    rolesRes,
    bidsRes,
    tariffRes,
    clausesRes,
    templatesRes,
    memoriesRes,
    calendarRes,
    threadsRes,
    ASSISTANTS,
  ] = await Promise.all([
    supabase.from('profiles').select('*'),
    supabase.from('user_roles').select('user_id, role'),
    supabase.from('bids').select('*').order('created_at', { ascending: false }),
    supabase.from('tariff_items').select('*').order('category'),
    supabase.from('library_clauses').select('*').order('updated_at', { ascending: false }),
    supabase.from('library_templates').select('*').order('updated_at', { ascending: false }),
    supabase.from('library_memories').select('*').order('year', { ascending: false }),
    supabase.from('calendar_events').select('*').order('event_date'),
    supabase.from('chat_threads').select('*').order('updated_at', { ascending: false }),
    loadAgentsSafe(),
  ]);

  const roleMap = {};
  (rolesRes.data || []).forEach((row) => {
    if (!roleMap[row.user_id] || (ROLE_RANK[row.role] || 0) > (ROLE_RANK[roleMap[row.user_id]] || 0)) {
      roleMap[row.user_id] = row.role;
    }
  });

  const USERS = (profilesRes.data || []).map((p) => mapUser(p, roleMap));

  const CURRENT_USER = {
    id: user?.id,
    name: profile?.display_name || user?.email?.split('@')[0] || 'Usuario',
    initials: initialsFrom(profile?.display_name || user?.email),
    role: primaryRole(roles),
    title: profile?.title || '',
    email: user?.email || '',
    hue: profile?.avatar_hue ?? 200,
  };

  const BIDS = (bidsRes.data || []).map(mapBid);

  const TARIFF = (tariffRes.data || []).map((r) => ({
    id: r.id, code: r.code, category: r.category, concept: r.concept, unit: r.unit,
    rate: Number(r.rate) || 0, cost: r.cost == null ? null : Number(r.cost), tag: r.tag, note: r.note,
  }));

  const clauses = (clausesRes.data || []).map((r) => ({
    id: r.id, title: r.title, tag: r.tag, uses: r.uses || 0,
    updated: (r.updated_at || '').slice(0, 10),
  }));
  const templates = (templatesRes.data || []).map((r) => ({
    id: r.id, title: r.title, type: r.type, sections: r.sections || 0, used: r.used || 0,
  }));
  const memories = (memoriesRes.data || []).map((r) => ({
    id: r.id, title: r.title, client: r.client, pages: r.pages,
    year: r.year, cpv: r.cpv, score: r.score,
  }));

  const CALENDAR = (calendarRes.data || []).map((r) => ({
    id: r.id, date: r.event_date, time: r.event_time, type: r.type,
    bid: r.bid_id, title: r.title, owner: r.owner,
  }));

  const threads = (threadsRes.data || []).map((r) => ({
    id: r.id, title: r.title, bid: r.bid_id, pinned: !!r.pinned,
    updated: relativeTime(r.updated_at),
  }));

  return {
    USERS,
    CURRENT_USER,
    ASSISTANTS,
    STATUS_LABELS,
    STATUS_FLOW,
    BIDS,
    SANDETEL: null,
    TARIFF,
    LIBRARY: { clauses, templates, memories },
    CALENDAR,
    MEMORY_INDEX: [],
    CHAT: { threads, messages: [] },
  };
}

// =====================================================
// Mutaciones
// =====================================================
function clean(obj) {
  // Elimina claves undefined para no pisar valores con undefined en updates
  const out = {};
  Object.keys(obj).forEach((k) => { if (obj[k] !== undefined) out[k] = obj[k]; });
  return out;
}

// ---------- Perfil propio ----------
// Cada usuario solo puede actualizar SU perfil (lo garantiza la RLS de profiles).
export async function updateProfile(userId, payload) {
  const patch = clean({
    display_name: payload.display_name,
    title: payload.title,
    avatar_hue: payload.avatar_hue,
  });
  const { data, error } = await supabase
    .from('profiles')
    .update(patch)
    .eq('user_id', userId)
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

// ---------- Licitaciones ----------
export async function createBid(payload, userId) {
  const insert = {
    code: payload.code || `EXPT ${new Date().getFullYear()}/${Math.floor(Math.random() * 9000 + 1000)}`,
    lot: payload.lot || 'Lote único',
    lot_number: payload.lotNumber ?? null,
    parent_bid_id: payload.parentBidId || null,
    title: payload.title,
    entity: payload.entity || null,
    entity_short: payload.entityShort || payload.entity || null,
    region: payload.region || null,
    company: payload.company || null,
    location: payload.location || null,
    cpv: payload.cpv || null,
    status: payload.status || 'analysis',
    analysis_status: payload.analysisStatus,
    analysis_phase: payload.analysisPhase,
    analysis_pct: payload.analysisPct,
    analysis_error: null,
    deadline: payload.deadline || null,
    team_lead: payload.teamLead || null,
    team: payload.team || [],
    created_by: userId,
  };
  const { data, error } = await supabase.from('bids').insert(insert).select().single();
  if (error) throw error;
  return mapBid(data);
}

export async function updateBid(id, payload) {
  const patch = clean({
    code: payload.code,
    lot: payload.lot,
    lot_number: payload.lotNumber,
    parent_bid_id: payload.parentBidId,
    title: payload.title,
    short_title: payload.shortTitle,
    entity: payload.entity,
    entity_short: payload.entityShort,
    region: payload.region,
    company: payload.company,
    location: payload.location,
    cpv: payload.cpv,
    status: payload.status,
    deadline: payload.deadline,
    budget_base: payload.budgetBase !== undefined ? (payload.budgetBase === null || payload.budgetBase === '' ? null : Number(payload.budgetBase) || 0) : undefined,
    analysis_source_url: payload.analysisSourceUrl,
    team_lead: payload.teamLead,
    team: payload.team,
    progress: payload.progress,
  });
  let { data, error } = await supabase.from('bids').update(patch).eq('id', id).select().single();
  // Si la columna short_title aún no existe (migración sin aplicar), guarda
  // el resto de cambios igualmente.
  if (error && 'short_title' in patch && /short_title/.test(error.message || '')) {
    delete patch.short_title;
    ({ data, error } = await supabase.from('bids').update(patch).eq('id', id).select().single());
  }
  if (error) throw error;
  return mapBid(data);
}

export async function deleteBid(id) {
  const { error } = await supabase.from('bids').delete().eq('id', id);
  if (error) throw error;
}

// Guarda los datos manuales del Análisis del Go (criterios automáticos manuales,
// tabla de competencia y la decisión final). Se fusiona con lo ya guardado.
export async function updateGoData(id, goData) {
  // Fusiona con lo ya guardado para que pestañas distintas (Go, Criterios…)
  // no se sobrescriban entre sí.
  let existing = {};
  try {
    const { data: cur } = await supabase.from('bids').select('go_data').eq('id', id).single();
    existing = (cur && cur.go_data) || {};
  } catch (_) { /* noop */ }
  const merged = { ...existing, ...(goData || {}) };
  const { data, error } = await supabase
    .from('bids')
    .update({ go_data: merged })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return mapBid(data);
}

// Devuelve los lotes (filas hijas) de una licitación matriz, ordenados por nº.
export async function listBidLots(parentBidId) {
  if (!parentBidId) return [];
  const { data, error } = await supabase
    .from('bids')
    .select('*')
    .eq('parent_bid_id', parentBidId)
    .order('lot_number', { ascending: true, nullsFirst: false });
  if (error) throw error;
  return (data || []).map(mapBid);
}

// ---------- Asistentes / agentes ----------
// Genera un slug estable a partir del nombre (a-z0-9-).
export function slugifyAgent(name) {
  return String(name || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '') // quita acentos
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48) || `agente-${Date.now().toString(36)}`;
}

export async function createAgent(payload, userId) {
  const insert = {
    slug: payload.slug || slugifyAgent(payload.name),
    name: payload.name,
    area: payload.area || null,
    phase: payload.phase ?? null,
    tagline: payload.tagline || null,
    summary: payload.summary || null,
    tone: payload.tone || null,
    icon: payload.icon || 'Sparkles',
    color: payload.color || 'sem-info',
    system_prompt: payload.systemPrompt || null,
    output_template: payload.outputTemplate || [],
    default_model: payload.defaultModel || 'auto',
    enabled: payload.enabled !== false,
    sort_order: payload.sortOrder ?? 0,
    created_by: userId,
  };
  const { data, error } = await supabase.from('agents').insert(insert).select().single();
  if (error) throw error;
  return mapAgent(data);
}

export async function updateAgent(id, payload) {
  const patch = clean({
    name: payload.name,
    area: payload.area,
    phase: payload.phase,
    tagline: payload.tagline,
    summary: payload.summary,
    tone: payload.tone,
    icon: payload.icon,
    color: payload.color,
    system_prompt: payload.systemPrompt,
    output_template: payload.outputTemplate,
    default_model: payload.defaultModel,
    enabled: payload.enabled,
    sort_order: payload.sortOrder,
  });
  const { data, error } = await supabase.from('agents').update(patch).eq('id', id).select().single();
  if (error) throw error;
  return mapAgent(data);
}

export async function deleteAgent(id) {
  const { error } = await supabase.from('agents').delete().eq('id', id);
  if (error) throw error;
}

// ---------- Conocimiento de los asistentes (F2) ----------
export async function listAgentKnowledge(agentId) {
  const { data, error } = await supabase.from('knowledge_sources')
    .select('id, title, kind, mime, size, status, error, chunk_count, file_path, applies_to_section, created_at')
    .eq('agent_id', agentId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// Lanza la ingesta en segundo plano (Gemini → chunks → embeddings). El servidor
// la completa aunque el cliente cierre la página; marca el error en BD si falla.
function triggerIngest(sourceId) {
  supabase.functions.invoke('ingest-knowledge', { body: { sourceId } })
    .then((r) => {
      if (r.error || r.data?.error) {
        const msg = r.error?.message || r.data?.error || 'No se pudo procesar el documento';
        return supabase.from('knowledge_sources').update({ status: 'error', error: String(msg).slice(0, 500) }).eq('id', sourceId);
      }
    })
    .catch(() => {});
}

// Sube un archivo al bucket privado, registra la fuente y dispara la ingesta.
export async function uploadAgentKnowledge(agentId, file, userId, appliesToSection = null) {
  const safeName = (file.name || 'archivo').replace(/[^\w.\-]+/g, '_');
  const path = `${agentId}/${Date.now()}-${safeName}`;
  const { error: upErr } = await supabase.storage.from('agent-knowledge').upload(path, file, { upsert: false });
  if (upErr) throw upErr;
  const { data, error } = await supabase.from('knowledge_sources').insert({
    agent_id: agentId,
    title: file.name || safeName,
    kind: 'document',
    file_path: path,
    mime: file.type || null,
    size: file.size ?? null,
    applies_to_section: appliesToSection || null,
    status: 'pending',
    created_by: userId,
  }).select().single();
  if (error) throw error;
  triggerIngest(data.id);
  return data;
}

// Añade una nota de texto como conocimiento (sin archivo).
export async function createAgentNote(agentId, { title, text, appliesToSection = null }, userId) {
  const structured = {
    title: title || 'Nota',
    summary: (text || '').slice(0, 280),
    sections: [{ heading: title || 'Nota', content: text || '' }],
  };
  const { data, error } = await supabase.from('knowledge_sources').insert({
    agent_id: agentId,
    title: title || 'Nota',
    kind: 'note',
    applies_to_section: appliesToSection || null,
    status: 'pending',
    structured_json: structured,
    created_by: userId,
  }).select().single();
  if (error) throw error;
  triggerIngest(data.id);
  return data;
}

export async function deleteKnowledgeSource(source) {
  const id = typeof source === 'string' ? source : source.id;
  const filePath = typeof source === 'object' ? source.file_path : null;
  const { error } = await supabase.from('knowledge_sources').delete().eq('id', id);
  if (error) throw error;
  if (filePath) { try { await supabase.storage.from('agent-knowledge').remove([filePath]); } catch (_) { /* best-effort */ } }
}

// ---------- Tarifario: ver src/data/budget-catalog.js ----------

// ---------- Biblioteca: cláusulas ----------
export async function createClause(payload, userId) {
  const { error } = await supabase.from('library_clauses').insert({
    title: payload.title, tag: payload.tag || null, body: payload.body || null, created_by: userId,
  });
  if (error) throw error;
}
export async function updateClause(id, payload) {
  const { error } = await supabase.from('library_clauses').update(clean({
    title: payload.title, tag: payload.tag, body: payload.body,
  })).eq('id', id);
  if (error) throw error;
}
export async function deleteClause(id) {
  const { error } = await supabase.from('library_clauses').delete().eq('id', id);
  if (error) throw error;
}

// ---------- Biblioteca: plantillas ----------
export async function createTemplate(payload, userId) {
  const { error } = await supabase.from('library_templates').insert({
    title: payload.title, type: payload.type || 'Plantilla',
    sections: Number(payload.sections) || 0, created_by: userId,
  });
  if (error) throw error;
}
export async function updateTemplate(id, payload) {
  const { error } = await supabase.from('library_templates').update(clean({
    title: payload.title, type: payload.type,
    sections: payload.sections !== undefined ? Number(payload.sections) || 0 : undefined,
  })).eq('id', id);
  if (error) throw error;
}
export async function deleteTemplate(id) {
  const { error } = await supabase.from('library_templates').delete().eq('id', id);
  if (error) throw error;
}

// ---------- Biblioteca: memorias ----------
export async function createMemory(payload, userId) {
  const { error } = await supabase.from('library_memories').insert({
    title: payload.title, client: payload.client || null,
    pages: payload.pages ? Number(payload.pages) : null,
    year: payload.year ? Number(payload.year) : null,
    cpv: payload.cpv || null, score: payload.score || null, created_by: userId,
  });
  if (error) throw error;
}
export async function updateMemory(id, payload) {
  const { error } = await supabase.from('library_memories').update(clean({
    title: payload.title, client: payload.client,
    pages: payload.pages !== undefined ? (payload.pages ? Number(payload.pages) : null) : undefined,
    year: payload.year !== undefined ? (payload.year ? Number(payload.year) : null) : undefined,
    cpv: payload.cpv, score: payload.score,
  })).eq('id', id);
  if (error) throw error;
}
export async function deleteMemory(id) {
  const { error } = await supabase.from('library_memories').delete().eq('id', id);
  if (error) throw error;
}

// ---------- Calendario ----------
export async function createEvent(payload, userId) {
  const { error } = await supabase.from('calendar_events').insert({
    title: payload.title, event_date: payload.date, event_time: payload.time || null,
    type: payload.type || 'milestone', bid_id: payload.bid || null,
    owner: payload.owner || userId, created_by: userId,
  });
  if (error) throw error;
}
export async function updateEvent(id, payload) {
  const { error } = await supabase.from('calendar_events').update(clean({
    title: payload.title, event_date: payload.date, event_time: payload.time,
    type: payload.type, bid_id: payload.bid, owner: payload.owner,
  })).eq('id', id);
  if (error) throw error;
}
export async function deleteEvent(id) {
  const { error } = await supabase.from('calendar_events').delete().eq('id', id);
  if (error) throw error;
}

// =====================================================
// IA — documentos, ficha, chat y memoria activa
// =====================================================

// Sube un documento al bucket privado, lo registra y extrae su texto.
// Devuelve cuando la extracción ha terminado (para que el análisis use el texto).
function isPdfFile(file) {
  return (file.type || '') === 'application/pdf' || /\.pdf$/i.test(file.name || '');
}

// Lanza la extracción de texto en segundo plano (no se espera). El servidor
// la completa aunque el cliente cierre la página. Marca el error en BD si falla.
function triggerExtraction(documentId) {
  supabase.functions
    .invoke('extract-document', { body: { documentId } })
    .then((extraction) => {
      if (extraction.error || extraction.data?.error) {
        const msg = extraction.error?.message || extraction.data?.error || 'No se pudo extraer el texto';
        return supabase.from('bid_documents').update({
          extract_status: 'error',
          extracted_text: `[Error de extracción: ${msg}]`,
        }).eq('id', documentId);
      }
    })
    .catch(() => {});
}

export async function uploadBidDocument(bidId, file, userId) {
  const safeName = file.name.replace(/[^\w.\-]+/g, '_');
  const path = `${bidId}/${Date.now()}-${safeName}`;
  const { error: upErr } = await supabase.storage.from('bid-documents').upload(path, file, { upsert: false });
  if (upErr) throw upErr;
  const { data, error } = await supabase.from('bid_documents').insert({
    bid_id: bidId, file_path: path, file_name: file.name,
    mime: file.type || 'application/pdf', size: file.size, created_by: userId,
  }).select().single();
  if (error) throw error;

  // Los PDFs se envían NATIVOS a Claude en el análisis (visión/OCR), así que
  // su extracción de texto NO es necesaria para analizar: la disparamos en
  // segundo plano (útil para el chat) y NO bloqueamos el arranque del análisis.
  // Para documentos no-PDF (DOCX, XLSX…) sí esperamos: el análisis usa su texto.
  if (isPdfFile(file)) {
    triggerExtraction(data.id);
  } else {
    const extraction = await supabase.functions.invoke('extract-document', { body: { documentId: data.id } });
    if (extraction.error || extraction.data?.error) {
      const msg = extraction.error?.message || extraction.data?.error || 'No se pudo extraer el texto';
      await supabase.from('bid_documents').update({
        extract_status: 'error',
        extracted_text: `[Error de extracción: ${msg}]`,
      }).eq('id', data.id);
    }
  }
  return data;
}

export async function listBidDocuments(bidId) {
  const { data } = await supabase.from('bid_documents')
    .select('id, file_name, file_path, mime, size, extract_status, created_at')
    .eq('bid_id', bidId).order('created_at', { ascending: true });
  return data || [];
}

// Genera una URL firmada temporal para abrir un documento del bucket privado.
export async function getBidDocumentUrl(documentId) {
  const { data: doc, error: e1 } = await supabase.from('bid_documents')
    .select('file_path').eq('id', documentId).maybeSingle();
  if (e1 || !doc?.file_path) throw new Error('Documento no encontrado');
  const { data, error } = await supabase.storage.from('bid-documents')
    .createSignedUrl(doc.file_path, 300);
  if (error) throw error;
  return data.signedUrl;
}


// Lanza el análisis IA de la ficha
export async function analyzeBid(bidId, sourceUrl, autofillBase = true, { forceRetry = false } = {}) {
  const { data, error } = await supabase.functions.invoke('bid-analyze', {
    body: { bidId, sourceUrl: sourceUrl || null, autofillBase, forceRetry },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

async function markAnalysisRetrying(bidId) {
  if (!bidId) return;
  const { error } = await supabase.from('bids').update({
    analysis_status: 'processing',
    analysis_phase: 'reading',
    analysis_pct: 10,
    analysis_error: null,
    analysis_warnings: [],
    analysis_started_at: new Date().toISOString(),
    analysis_completed_at: null,
  }).eq('id', bidId);
  if (error) console.warn('[lizzi] no se pudo marcar el reintento antes de invocar', error.message);
  emitAnalysis(bidId, 'reading', { pct: 10 });
}

// Orquesta creación → subida+extracción → análisis (en segundo plano)
export async function runBidIngestion(bidId, files, sourceUrl, userId) {
  for (const f of files) {
    await uploadBidDocument(bidId, f, userId);
  }
  return analyzeBid(bidId, sourceUrl);
}

// Helper de eventos para la ventana de progreso premium
function emitAnalysis(bidId, phase, extra = {}) {
  window.dispatchEvent(new CustomEvent('lizzi:analysis-progress', {
    detail: { bidId, phase, ...extra },
  }));
}

// Sondea el estado del análisis en la BD hasta que termine (done) o falle (error).
// Emite eventos de progreso a partir del estado REAL persistido, no de una
// animación local. Así la UI siempre refleja lo que ocurre de verdad.
export async function pollAnalysisStatus(bidId, { onTick } = {}) {
  const DEADLINE = Date.now() + 6 * 60_000; // 6 min máx de sondeo
  while (Date.now() < DEADLINE) {
    await new Promise((r) => setTimeout(r, 2500));
    const { data } = await supabase
      .from('bids')
      .select('analysis_status, analysis_phase, analysis_pct, analysis_error')
      .eq('id', bidId)
      .maybeSingle();
    if (!data) continue;
    const status = data.analysis_status;
    const pct = typeof data.analysis_pct === 'number' ? data.analysis_pct : 0;
    if (onTick) onTick({ status, phase: data.analysis_phase, pct, error: data.analysis_error });

    if (status === 'done') {
      emitAnalysis(bidId, 'done', { pct: 100 });
      window.dispatchEvent(new CustomEvent('lizzi:bid-analyzed', { detail: bidId }));
      return { status: 'done' };
    }
    if (status === 'error') {
      const msg = data.analysis_error || 'El análisis falló.';
      emitAnalysis(bidId, 'error', { message: msg });
      window.dispatchEvent(new CustomEvent('lizzi:bid-analyze-error', { detail: { bidId, message: msg } }));
      return { status: 'error', message: msg };
    }
    // Sigue procesando: reflejamos la FASE y el % reales de la BD.
    emitAnalysis(bidId, data.analysis_phase || 'analyzing', { pct });
  }
  return { status: 'timeout' };
}

// Orquesta el flujo completo emitiendo eventos de progreso para el overlay.
// fases: uploading → extracting → analyzing → done | error
// La generación con Gemini corre en segundo plano en la Edge Function; aquí
// solo arrancamos y OBSERVAMOS el estado persistido en la BD.
export async function runAnalysisFlow({ bidId, title, files = [], sourceUrl = null, userId, autofillBase = true }) {
  window.dispatchEvent(new CustomEvent('lizzi:analysis-open', { detail: { bidId, title } }));
  try {
    if (files.length) {
      emitAnalysis(bidId, 'uploading', { total: files.length, done: 0 });
      let done = 0;
      for (const f of files) {
        await uploadBidDocument(bidId, f, userId);
        done += 1;
        emitAnalysis(bidId, 'uploading', { done, total: files.length });
      }
    }
    // A partir de aquí manda el backend: reading → analyzing → compiling → done.
    // Claude recibe los PDF nativos y genera la ficha en una sola llamada.
    emitAnalysis(bidId, 'reading');
    await analyzeBid(bidId, sourceUrl, autofillBase); // arranca el trabajo en segundo plano
    const result = await pollAnalysisStatus(bidId);
    if (result.status === 'error') throw new Error(result.message);
    if (result.status === 'timeout') {
      // No es un fallo: el análisis sigue en segundo plano. Avisamos sin
      // pintar el overlay en rojo y dejamos que la ficha lo refresque sola.
      window.dispatchEvent(new CustomEvent('lizzi:toast', { detail: { text: 'El análisis sigue en marcha. Puedes abrir la ficha para ver el progreso.', icon: 'Sparkles' } }));
    }
  } catch (e) {
    emitAnalysis(bidId, 'error', { message: e?.message || 'error' });
    window.dispatchEvent(new CustomEvent('lizzi:bid-analyze-error', { detail: { bidId, message: e?.message || 'error' } }));
    throw e;
  }
}

// Reintenta el análisis de una licitación ya creada (sin volver a subir PDFs).
export async function retryAnalysis(bidId, sourceUrl = null, autofillBase = true) {
  await markAnalysisRetrying(bidId);
  try {
    await analyzeBid(bidId, sourceUrl, autofillBase, { forceRetry: true });
  } catch (e) {
    const message = e?.message || 'No se pudo relanzar el análisis.';
    await supabase.from('bids').update({
      analysis_status: 'error',
      analysis_phase: 'error',
      analysis_error: message,
      analysis_completed_at: new Date().toISOString(),
    }).eq('id', bidId);
    emitAnalysis(bidId, 'error', { message });
    throw e;
  }
  return pollAnalysisStatus(bidId);
}



export async function fetchBid(bidId) {
  const { data, error } = await supabase.from('bids').select('*').eq('id', bidId).maybeSingle();
  if (error) throw error;
  return data;
}

// Vuelve a leer una licitación de la BD, la sustituye en el store global
// (window.MockData.BIDS) y avisa a las pantallas abiertas con el evento
// `lizzi:bid-updated` para que se refresquen sin recargar la página.
export async function refreshBidInStore(bidId) {
  if (!bidId) return null;
  try {
    const row = await fetchBid(bidId);
    if (!row) return null;
    const mapped = mapBid(row);
    const arr = window.MockData?.BIDS;
    if (Array.isArray(arr)) {
      const idx = arr.findIndex((b) => b.id === bidId);
      if (idx >= 0) arr[idx] = mapped; else arr.push(mapped);
    }
    window.dispatchEvent(new CustomEvent('lizzi:bid-updated', { detail: bidId }));
    return mapped;
  } catch (_) {
    return null;
  }
}

// Genera con IA el título resumido de las licitaciones indicadas (o de todas
// las que aún no lo tienen si no se pasan ids). Con `force` lo regenera.
export async function generateShortTitles({ bidIds = null, force = false } = {}) {
  const { data, error } = await supabase.functions.invoke('bid-short-title', { body: { bidIds, force } });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  const titles = data?.titles || {};
  const arr = window.MockData?.BIDS;
  if (Array.isArray(arr)) {
    arr.forEach((b, i) => { if (titles[b.id]) arr[i] = { ...b, shortTitle: titles[b.id] }; });
  }
  Object.keys(titles).forEach((id) => window.dispatchEvent(new CustomEvent('lizzi:bid-updated', { detail: id })));
  return titles;
}

// ---------- Presupuesto de oferta (go_data.budget) ----------
// Lanza la propuesta de presupuesto con IA (llamada independiente del análisis)
// y espera a que termine leyendo go_data.budgetJob. Devuelve la fila de la BD.
export async function generateBudget(bidId, { onTick, timeoutMs = 8 * 60_000 } = {}) {
  const { data, error } = await supabase.functions.invoke('bid-budget', { body: { bidId } });
  if (error) {
    let msg = error.message;
    try { msg = (await error.context?.json())?.error || msg; } catch (_) { /* noop */ }
    throw new Error(msg);
  }
  if (data?.error) throw new Error(data.error);
  return waitBudgetJob(bidId, { onTick, timeoutMs });
}

// Espera a que termine la generación en curso (go_data.budgetJob).
export async function waitBudgetJob(bidId, { onTick, timeoutMs = 8 * 60_000 } = {}) {
  const t0 = Date.now();
  for (;;) {
    await new Promise((r) => setTimeout(r, 4000));
    const row = await fetchBid(bidId);
    const job = row?.go_data?.budgetJob || {};
    onTick?.(Date.now() - t0);
    if (job.status === 'done' || job.status === 'skipped') return row;
    if (job.status === 'error') throw new Error(job.error || 'No se pudo generar el presupuesto');
    if (Date.now() - t0 > timeoutMs) throw new Error('La propuesta de presupuesto está tardando más de lo normal. Vuelve a abrir la pestaña en unos minutos.');
  }
}

// Guarda las ediciones del equipo. `editedAt` hace que un reanálisis no las sustituya.
export async function saveBudget(bidId, budget) {
  const now = new Date().toISOString();
  return updateGoData(bidId, { budget: { ...budget, updatedAt: now, editedAt: now } });
}

// Intercambia la versión actual del presupuesto con la anterior (deshacer / rehacer regeneración).
export async function swapBudgetVersion(bidId, current, previous) {
  return updateGoData(bidId, { budget: previous, budgetPrevious: current });
}

// ---------- Chat con Lizzi ----------
export async function listThreads() {
  const { data } = await supabase.from('chat_threads').select('*').order('updated_at', { ascending: false });
  return data || [];
}
export async function createThread({ bidId, title }, userId) {
  const { data, error } = await supabase.from('chat_threads').insert({
    user_id: userId, bid_id: bidId || null, title: title || 'Nueva conversación',
  }).select().single();
  if (error) throw error;
  return data;
}
export async function loadThreadMessages(threadId) {
  const { data } = await supabase.from('chat_messages')
    .select('id, role, content, created_at')
    .eq('thread_id', threadId).order('created_at', { ascending: true });
  return data || [];
}
export async function sendChatMessage(threadId, message) {
  const { data, error } = await supabase.functions.invoke('bid-chat', { body: { threadId, message } });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}
export async function renameThread(id, title) {
  const { error } = await supabase.from('chat_threads').update({ title }).eq('id', id);
  if (error) throw error;
}
export async function togglePinThread(id, pinned) {
  const { error } = await supabase.from('chat_threads').update({ pinned }).eq('id', id);
  if (error) throw error;
}
export async function deleteThread(id) {
  await supabase.from('chat_messages').delete().eq('thread_id', id);
  const { error } = await supabase.from('chat_threads').delete().eq('id', id);
  if (error) throw error;
}

// ---------- Memoria activa ----------
export async function loadBidMemory(bidId, { includeArchived = false } = {}) {
  let q = supabase.from('bid_memory').select('*').order('created_at', { ascending: false });
  if (bidId) q = q.eq('bid_id', bidId);
  if (!includeArchived) q = q.eq('archived', false);
  const { data } = await q;
  return data || [];
}

export async function createMemoryEntry({ bid_id, kind = 'fact', content }) {
  const { data: auth } = await supabase.auth.getUser();
  const uid = auth?.user?.id;
  if (!uid) throw new Error('Sesión no encontrada');
  const { data, error } = await supabase.from('bid_memory').insert({
    bid_id: bid_id || null,
    kind,
    content,
    source: 'manual',
    created_by: uid,
  }).select().single();
  if (error) throw error;
  return data;
}

export async function updateMemoryEntry(id, { kind, content }) {
  const patch = {};
  if (kind !== undefined) patch.kind = kind;
  if (content !== undefined) patch.content = content;
  const { data, error } = await supabase.from('bid_memory').update(patch).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

export async function setMemoryArchived(id, archived) {
  const { data, error } = await supabase.from('bid_memory').update({ archived }).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

export async function deleteMemoryEntry(id) {
  const { error } = await supabase.from('bid_memory').delete().eq('id', id);
  if (error) throw error;
}
